import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../services/authService';
import { apiGlobal } from '../services/api';
import { getApiUrl } from '../utils/config';
import { SANDBOX_GROUP_NAME } from '../guides/sandboxSeed';
import { useUserSession } from './UserSessionContext.jsx';
import cacheService from '../services/cacheService';
import payeeCacheService from '../services/payeeCache';
import { invalidateListCache, BUDGETS_LIST_CACHE_KEY, LEDGERS_LIST_CACHE_KEY } from '../utils/listCacheUtil';

// Client caches (cacheService, payeeCache, listCacheUtil) are global, not keyed
// by groupId. On an active-group switch the prior group's payees / budgets /
// ledgers would otherwise be served until TTL, letting the user act on another
// group's resource ids (the guide sandbox surfaced main-group payees -> the API
// rejected line saves with "payee not found in this group"). Flush them on
// every real switch so the destination always refetches for the new group (#855).
const flushGroupScopedCaches = () => {
  if (typeof window === 'undefined') return;
  cacheService.clear();
  payeeCacheService.invalidatePayeeCache('group_switch');
  invalidateListCache(BUDGETS_LIST_CACHE_KEY);
  invalidateListCache(LEDGERS_LIST_CACHE_KEY);
};

const STORAGE_KEY = 'budgetrecon.activeGroupId';

export const GroupContext = createContext(null);

// Resolve the path to redirect to after a group switch. Resource-id-bearing
// detail routes are unsafe (the new group probably doesn't have ledger 8 or
// budget 3), so collapse to the section root. Other routes (dashboard,
// members, account, etc.) re-fetch via activeGroupId from context, so stay.
const resolveRedirectAfterGroupSwitch = (pathname) => {
  if (/^\/ledgers(\/|$)/.test(pathname)) return '/ledgers';
  if (/^\/budgets(\/|$)/.test(pathname)) return '/budgets';
  return null; // null = no navigate needed
};

export const GroupProvider = ({ children }) => {
  const session = useUserSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({
    loading: true,
    groups: [],
    activeGroupId: null,
    error: null
  });
  // Mirrors state so createSandboxGroup can read the current group list
  // without a stale closure (its dedup guard must see the latest groups).
  const stateRef = useRef(state);
  stateRef.current = state;

  const loadGroups = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${getApiUrl()}/groups`);
      if (!res.ok) throw new Error(`listGroups failed: ${res.status}`);
      const data = await res.json();
      const groups = data.groups || [];

      const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
      // Never land on a hidden guide-sandbox group as the active workspace - a
      // guide swap may have persisted its id to localStorage. Pick only from
      // real workspaces so an interrupted guide can't strand the user (#812).
      const pickable = groups.filter(g => !g.guideSandbox);
      // A reload (rename, role change, invite accept) keeps the workspace the
      // user is on: the in-memory id wins while it is still in the list. The
      // stored id only decides on first load or once the current one is gone,
      // so localStorage drift (another tab switched, key cleared) no longer
      // flips the page to the first workspace (#1795). loadGroups never writes
      // the key; that stays with setActiveGroupId.
      const current = stateRef.current.activeGroupId;
      const pick = pickable.find(g => g.groupId === current)
        || pickable.find(g => g.groupId === stored)
        || pickable[0]
        || null;
      const nextId = pick ? pick.groupId : null;

      // A reload that drops the active workspace (the user deleted or left it,
      // or an owner removed them) moves the user without going through
      // setActiveGroupId, so the #855 cache flush has to run here as well.
      // Otherwise the fallback workspace serves the removed one's payees,
      // budgets, and ledgers until TTL (#1801). First load has nothing stale.
      if (current != null && nextId !== current) flushGroupScopedCaches();

      setState({
        loading: false,
        groups,
        activeGroupId: nextId,
        error: null
      });
    } catch (err) {
      setState(s => ({ ...s, loading: false, error: err.message }));
    }
  }, []);

  useEffect(() => {
    if (session.loading || !session.user) return;
    loadGroups();
  }, [session.loading, session.user, loadGroups]);

  // options.keepPath (#1710): suppress the #648 collapse only. A deep link
  // (?group= from a summary email) carries a resource id that belongs to the
  // group being switched TO, so the collapse premise below does not hold and
  // the caller owns the destination. Everything else - membership guard,
  // localStorage write, cache flush - runs identically.
  const setActiveGroupId = useCallback((groupId, options = {}) => {
    // Decide off the current state ref so the switch side effects (cache flush,
    // redirect) run synchronously and deterministically, not from inside the
    // setState updater whose timing we cannot rely on.
    const s = stateRef.current;
    if (!s.groups.some(g => g.groupId === groupId)) return;
    if (s.activeGroupId === groupId) return;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, groupId);
    }
    setState(prev => ({ ...prev, activeGroupId: groupId }));
    // Flush group-scoped client caches before redirecting so the destination
    // refetches for the new group instead of serving the prior group's lists (#855).
    flushGroupScopedCaches();
    if (options.keepPath) return;
    // Drop to a safe section root if current URL holds a resource id that
    // belongs to the previous group (#648).
    const target = resolveRedirectAfterGroupSwitch(location.pathname);
    if (target && target !== location.pathname) {
      navigate(target);
    }
  }, [location.pathname, navigate]);

  // Create the caller's hidden guide-sandbox workspace and register it in
  // state so it can be made active immediately. One sandbox per user:
  // idempotent - if one already exists it is returned without a POST.
  // The group is hidden from the workspace switcher (filtered by guideSandbox).
  const createSandboxGroup = useCallback(async () => {
    const existing = stateRef.current.groups.find(g => g.guideSandbox === true);
    if (existing) return existing;

    const res = await apiGlobal.post('/groups', {
      name: SANDBOX_GROUP_NAME,
      guideSandbox: true
    });
    const created = { ...res.group, membership: { role: 'owner' } };
    setState(s => (
      s.groups.some(g => g.groupId === created.groupId)
        ? s
        : { ...s, groups: [...s.groups, created] }
    ));
    return created;
  }, []);

  // Create a real (non-sandbox) workspace and register it in state so it shows
  // in the switcher / lists immediately. The backend enforces the per-owner
  // creation cap (#753) and returns 403 workspace_limit_reached at the limit;
  // the caller surfaces that. The creator is the owner, so it counts toward
  // their cap. Does not auto-switch - the caller decides.
  const createWorkspace = useCallback(async (name) => {
    const res = await apiGlobal.post('/groups', { name });
    const created = { ...res.group, membership: { role: 'owner' } };
    setState(s => (
      s.groups.some(g => g.groupId === created.groupId)
        ? s
        : { ...s, groups: [...s.groups, created] }
    ));
    return created;
  }, []);

  const value = useMemo(() => {
    const active = state.groups.find(g => g.groupId === state.activeGroupId) || null;
    return {
      loading: state.loading,
      error: state.error,
      groups: state.groups,
      activeGroupId: state.activeGroupId,
      activeGroup: active,
      activeGroupName: active ? active.name : null,
      userRole: active && active.membership ? active.membership.role : null,
      setActiveGroupId,
      createSandboxGroup,
      createWorkspace,
      reload: loadGroups
    };
  }, [state, setActiveGroupId, createSandboxGroup, createWorkspace, loadGroups]);

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
};

export const useGroup = () => {
  const ctx = useContext(GroupContext);
  if (!ctx) throw new Error('useGroup must be used within GroupProvider');
  return ctx;
};
